import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as ArrowDownIcon } from "../assets/icons/arrow-down.svg";
import { ReactComponent as BillingIcon } from "../assets/icons/billing.svg";
import { ReactComponent as SettingIcon } from "../assets/icons/setting.svg";
import { ReactComponent as DashboardIcon } from "../assets/icons/dashboard.svg";
import { ReactComponent as DocumentsIcon } from "../assets/icons/documents.svg";
import { ReactComponent as LogoutIcon } from "../assets/icons/logout.svg";
import { ReactComponent as MarketingIcon } from "../assets/icons/marketing.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg";
import { ReactComponent as UsersIcon } from "../assets/icons/users.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import { ReactComponent as PulseIcon } from "../assets/icons/pulse.svg";
import { ReactComponent as UserIcon } from "../assets/images/user.svg";
import { getUser } from "../helpers/utils";
import { Logo } from "./Logo";
import { useCrmEnabled } from "../helpers/hooks/use-crm-enabled";
import { useTrackingHealthEnabled } from "../helpers/hooks/use-tracking-health-enabled";
import { api } from "../helpers/api";

export function MenuItem({
  Icon,
  title,
  isActive,
  sidebarExpanded = false,
  to,
  disabled = false,
  onClick,
}: {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  isActive: boolean;
  sidebarExpanded?: boolean;
  to: string;
  disabled?: boolean;
  onClick?: () => void;
}) {
  if (disabled) {
    return (
      <div className="self-stretch p-3 rounded justify-start items-center gap-[7px] flex">
        <Icon className={"w-4 h-4 [&_path]:!stroke-content-tertiary"} />
        {sidebarExpanded && (
          <div className="text-center text-content-tertiary text-sm font-medium leading-[14px]">
            {title}
          </div>
        )}
      </div>
    );
  }
  return (
    <Link
      to={to}
      className={`self-stretch p-3 hover:bg-[#EDEBEA]/70 ${
        isActive
          ? "!bg-[#fff] border border-[#E1DEDD] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] rounded-[6px]"
          : ""
      } rounded justify-start items-center gap-[7px] flex`}
      onClick={onClick}
    >
      <Icon
        className={`w-4 h-4 ${
          isActive ? "[&_path]:!stroke-black" : "[&_path]:!stroke-[#000000]"
        }`}
      />
      {sidebarExpanded && (
        <div className="text-center text-content-primary text-sm font-medium leading-[14px]">
          {title}
        </div>
      )}
    </Link>
  );
}

export function Sidebar({
  sidebarExpanded,
  setSidebarExpanded,
  isMobile = false,
  onClose,
}: {
  sidebarExpanded: boolean;
  setSidebarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile?: boolean;
  onClose?: () => void;
}) {
  const user = getUser();
  const location = useLocation();
  const path = location.pathname;
  const crmEnabled = useCrmEnabled();
  const trackingHealthEnabled = useTrackingHealthEnabled();
  const [isChargeManager, setIsChargeManager] = useState(false);
  useEffect(() => {
    api.chargeAuth
      .me()
      .then((r) => setIsChargeManager(!!r.isManager))
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
    // api.deleteOnboarding(user.id).then(() => {
    // });
  };

  return (
    <div
      className={`${
        sidebarExpanded
          ? isMobile
            ? "w-full"
            : "min-w-[214px] w-[214px]"
          : "w-[72px]"
      } h-[100dvh] bg-surface-tertiary shadow-soft flex-col justify-start items-center flex border-r border-border-primary`}
    >
      <div
        className={`pl-4 w-full pr-3 py-4 border-b border-border-secondary ${
          sidebarExpanded ? "justify-between" : "justify-center"
        } items-center flex`}
      >
        {sidebarExpanded && (
          <Logo className="!text-[28px] !tracking-[-1.13px] !leading-[28.26px]" />
        )}
        <button
          type="button"
          onClick={() =>
            isMobile ? onClose?.() : setSidebarExpanded((prev) => !prev)
          }
          className="opacity-70 p-[3px] bg-surface-secondary rounded shadow-soft border border-border-primary justify-center items-center gap-2.5 flex"
        >
          {isMobile ? (
            <CloseIcon />
          ) : (
            <ArrowDownIcon
              className={`w-4 h-4 ${
                sidebarExpanded ? "rotate-90" : "-rotate-90"
              }`}
            />
          )}
        </button>
      </div>
      <div className="w-full flex flex-col justify-between h-[calc(100dvh-62px)]">
        <div className="px-4 pt-4 pb-5 flex-col justify-start items-start flex">
          <div className="self-stretch flex-col justify-start items-start gap-2 flex">
            <MenuItem
              Icon={DashboardIcon}
              title="Dashboard"
              isActive={path === "/dashboard"}
              sidebarExpanded={sidebarExpanded}
              to="/dashboard"
              onClick={onClose}
            />
            <MenuItem
              Icon={UsersIcon}
              title="Manage users"
              isActive={path === "/users"}
              sidebarExpanded={sidebarExpanded}
              to="/users"
              onClick={onClose}
            />
            <MenuItem
              Icon={DocumentsIcon}
              title="AI Document Library"
              isActive={path === "/documents"}
              sidebarExpanded={sidebarExpanded}
              to="/documents"
              onClick={onClose}
            />
            <MenuItem
              Icon={MarketingIcon}
              title="Marketing"
              isActive={path === "/marketing" || path.startsWith("/marketing?")}
              sidebarExpanded={sidebarExpanded}
              to="/marketing"
              onClick={onClose}
            />
            <MenuItem
              Icon={BillingIcon}
              title="Billing"
              isActive={path === "/billing" || path.startsWith("/billing?")}
              sidebarExpanded={sidebarExpanded}
              to="/billing"
              onClick={onClose}
            />
            {isChargeManager && (
              <MenuItem
                Icon={SettingIcon}
                title="Charge Security"
                isActive={path.startsWith("/settings/charge-security")}
                sidebarExpanded={sidebarExpanded}
                to="/settings/charge-security"
                onClick={onClose}
              />
            )}
            <MenuItem
              Icon={UsersIcon}
              title="Customer Success"
              isActive={
                path === "/customer-success" ||
                path.startsWith("/customer-success?")
              }
              sidebarExpanded={sidebarExpanded}
              to="/customer-success"
              onClick={onClose}
            />
            <MenuItem
              Icon={SearchIcon}
              title="Metasearch"
              isActive={path === "/metasearch" || path.startsWith("/metasearch")}
              sidebarExpanded={sidebarExpanded}
              to="/metasearch"
              onClick={onClose}
            />
            {trackingHealthEnabled ? (
              <MenuItem
                Icon={PulseIcon}
                title="Tracking"
                isActive={path.startsWith("/tracking")}
                sidebarExpanded={sidebarExpanded}
                to="/tracking/health"
                onClick={onClose}
              />
            ) : (
              <MenuItem
                Icon={MarketingIcon}
                title="Onboarding Tracking"
                isActive={
                  path === "/onboarding-tracking" ||
                  path.startsWith("/onboarding-tracking")
                }
                sidebarExpanded={sidebarExpanded}
                to="/onboarding-tracking"
                onClick={onClose}
              />
            )}
            {crmEnabled && (
              <MenuItem
                Icon={UsersIcon}
                title="CRM"
                isActive={
                  path.startsWith("/crm/customers") || path === "/crm"
                }
                sidebarExpanded={sidebarExpanded}
                to="/crm/customers"
                onClick={onClose}
              />
            )}
            {/* Ask Autumn moved to a floating assistant (AskAutumnWidget) —
                reachable from every page via the bottom-right button instead
                of the sidebar. Keep this block out to avoid confusion. */}
          </div>
        </div>
        <div
          className={`self-stretch px-4 py-5 ${
            sidebarExpanded ? "justify-between" : "justify-center"
          } items-center gap-[15px] flex`}
        >
          <div className={"h-6 justify-start items-center gap-2.5 flex"}>
            <UserIcon className="w-6 h-6 rounded-[47px]" />
            {sidebarExpanded && (
              <div className="flex-col justify-start items-start gap-1.5 flex">
                <div className="text-content-primary text-base font-semibold">
                  Admin
                </div>
              </div>
            )}
          </div>
          {sidebarExpanded && (
            <button
              type="button"
              className="hover:opacity-70"
              onClick={handleLogout}
            >
              <LogoutIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
